import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Container, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import swal from 'sweetalert';
import SalesListResults from '../../components/Vendas/SalesListResults';
import SalesListToolbar from '../../components/Vendas/SalesListToolbar';
import affiliateService from '../../services/affiliateService';

const SalesList = () => {
  const auth = useSelector((state) => state.auth);
  const [isLoadingRegs, setIsLoadingRegs] = useState(true);
  const [regs, setRegs] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const get = async () => {
    setIsLoadingRegs(true);
    setRegs([]);

    try {
      const params = {
        page,
        per_page: perPage
      };

      if (dateFrom) {
        params.date_from = dateFrom;
      }

      if (dateTo) {
        params.date_to = dateTo;
      }

      const response = await affiliateService.getSales(auth.token, params);

      if (!response || !response.data) {
        throw new Error('Erro ao buscar vendas');
      }

      setRegs(response.data.data || []);
      setPage(response.data.page || 1);
      setPerPage(response.data.perPage || 25);
      setTotal(response.data.total || 0);
    } catch (e) {
      console.error(e);
      swal('Erro', 'Erro ao buscar vendas', 'error');
    } finally {
      setIsLoadingRegs(false);
    }
  };

  useEffect(() => {
    get();
  }, [page, perPage, dateFrom, dateTo]);

  const requireAdmin = () => {
    if (!auth.user.is_admin) {
      swal('Erro', 'Apenas administradores podem alterar vendas', 'error');
      return false;
    }
    return true;
  };

  const runCommissionTransition = async (serviceCall, successMessage, errorMessage) => {
    if (!requireAdmin()) return;
    try {
      const response = await serviceCall();
      if (response && response.data) {
        swal('Sucesso', successMessage, 'success');
        get();
      } else {
        throw new Error(errorMessage);
      }
    } catch (e) {
      console.error(e);
      const apiMessage = e?.response?.data?.error || errorMessage;
      swal('Erro', apiMessage, 'error');
    }
  };

  const handleConfirmPayment = (commissionId) =>
    runCommissionTransition(
      () => affiliateService.confirmPaymentCommission(commissionId, auth.token),
      'Pagamento confirmado — comissão movida para "A Liberar"',
      'Erro ao confirmar pagamento'
    );

  const handleRelease = (commissionId) =>
    runCommissionTransition(
      () => affiliateService.releaseCommission(commissionId, auth.token),
      'Comissão liberada para pagamento',
      'Erro ao liberar comissão'
    );

  const handlePay = (commissionId) =>
    runCommissionTransition(
      () => affiliateService.payCommission(commissionId, auth.token),
      'Comissão marcada como paga',
      'Erro ao marcar comissão como paga'
    );

  const handleCancelCommission = async (commissionId) => {
    if (!requireAdmin()) return;

    const confirmCancel = await swal({
      title: 'Cancelar comissão?',
      text: 'Essa ação não pode ser desfeita. A venda permanece registrada, mas a comissão associada fica como cancelada.',
      icon: 'warning',
      buttons: ['Voltar', 'Cancelar comissão'],
      dangerMode: true
    });

    if (!confirmCancel) return;

    await runCommissionTransition(
      () => affiliateService.cancelCommission(commissionId, auth.token),
      'Comissão cancelada',
      'Erro ao cancelar comissão'
    );
  };

  const handleDelete = async (id) => {
    if (!requireAdmin()) return;

    const confirmDelete = await swal({
      title: 'Tem certeza?',
      text: 'Deseja realmente deletar esta venda? A comissão associada também será removida.',
      icon: 'warning',
      buttons: ['Cancelar', 'Deletar'],
      dangerMode: true
    });

    if (!confirmDelete) return;

    try {
      const response = await affiliateService.deleteSale(id, auth.token);

      if (response) {
        swal('Sucesso', 'Venda deletada com sucesso!', 'success');
        get();
      } else {
        throw new Error('Erro ao deletar venda');
      }
    } catch (e) {
      console.error(e);
      swal('Erro', 'Erro ao deletar venda', 'error');
    }
  };

  return (
    <>
      <Helmet>
        <title>Vendas | Sistema de Afiliados</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth={false}>
          <SalesListToolbar
            dateFrom={dateFrom}
            setDateFrom={setDateFrom}
            dateTo={dateTo}
            setDateTo={setDateTo}
            onRefresh={get}
          />
          {isLoadingRegs && (
            <Box sx={{ pt: 3 }}>
              <Typography align="center" variant="h5" color="primary">
                <CircularProgress color="primary" sx={{ mb: 3 }} />
                <br />
                Buscando informações, por favor aguarde...
              </Typography>
            </Box>
          )}
          {!isLoadingRegs && (!regs || regs.length === 0) && (
            <Box sx={{ pt: 3 }}>
              <Typography align="center" variant="h5" color="primary">
                Nenhuma venda localizada
              </Typography>
            </Box>
          )}
          {!isLoadingRegs && regs && regs.length > 0 && (
            <Box sx={{ pt: 3 }}>
              <SalesListResults
                regs={regs}
                page={page}
                setPage={setPage}
                perPage={perPage}
                setPerPage={setPerPage}
                total={total}
                onDelete={handleDelete}
                onConfirmPayment={handleConfirmPayment}
                onRelease={handleRelease}
                onPay={handlePay}
                onCancel={handleCancelCommission}
                isAdmin={auth.user.is_admin}
              />
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default SalesList;
